import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import { Flame, WhatsappLogo, Eye } from "@phosphor-icons/react";
import WhatsAppSendModal from "./WhatsAppSendModal";
import { toast } from "sonner";

/**
 * Phase 4b-3: Hottest Customers — Last 7 Days widget.
 * Reads /api/whatsapp/dashboard/hottest-customers (uses public_doc_access_logs).
 * One-click "Follow-up on WhatsApp" opens the reusable WhatsAppSendModal
 * pre-populated with the "hot_customer_followup" template.
 */
const HottestCustomersWidget = ({ user, days = 7, limit = 5 }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [waModal, setWaModal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        requester_role: user?.role || "",
        requester_user_id: user?.user_id || "",
        days: String(days),
        limit: String(limit),
      });
      const r = await axios.get(`${API}/whatsapp/dashboard/hottest-customers?${params.toString()}`);
      setItems(r.data || []);
    } catch (e) {
      // Widget never blocks dashboard
    } finally {
      setLoading(false);
    }
  }, [user?.role, user?.user_id, days, limit]);

  useEffect(() => { load(); }, [load]);

  const followUp = (c) => {
    if (!c.phone) { toast.error("No phone number on this customer"); return; }
    const docTypeLabel = (c.doc_type || "document").replace(/-/g, " ");
    setWaModal({
      module: "lead",
      defaultAction: "hot_customer_followup",
      receiver: { name: c.customer_name, phone: c.phone, type: "customer" },
      context: {
        variables: {
          name: c.customer_name,
          customer_name: c.customer_name,
          document_type: docTypeLabel,
          document_number: c.doc_number || "",
        },
      },
    });
  };

  return (
    <>
      <div data-testid="hottest-customers-widget" className="bg-white border border-slate-200">
        <div className="border-b border-slate-200 px-5 py-3 flex items-center justify-between bg-gradient-to-r from-rose-50 to-white">
          <div className="flex items-center gap-2">
            <Flame size={16} weight="fill" className="text-rose-600" />
            <h3 className="text-sm uppercase tracking-[0.2em] font-bold text-slate-700">Hottest Customers</h3>
            <span className="text-[10px] uppercase tracking-wider font-bold text-rose-700 bg-rose-50 border border-rose-200 px-1.5 py-0.5">{days}d</span>
          </div>
          <span className="text-[10px] text-slate-500">top {items.length} of {limit}</span>
        </div>
        <div className="divide-y divide-slate-100">
          {loading ? (
            <div className="text-xs text-slate-500 p-5">Loading…</div>
          ) : items.length === 0 ? (
            <div className="text-xs text-slate-400 text-center py-8">
              <Eye size={20} className="mx-auto mb-2 opacity-40" />
              No public PDFs opened in the last {days} days yet.
            </div>
          ) : (
            items.map((c, i) => (
              <div key={c.customer_name + i} data-testid={`hot-cust-${i}`} className="p-4">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 text-sm truncate" data-testid={`hot-cust-name-${i}`}>{c.customer_name}</div>
                    {c.company_name && c.company_name !== c.customer_name && <div className="text-xs text-slate-500 truncate">{c.company_name}</div>}
                    <div className="text-[10px] text-slate-500 mt-1 flex flex-wrap gap-x-2">
                      <span className="capitalize">{(c.doc_type || "").replace(/-/g, " ")}</span>
                      {c.doc_number && <span className="font-mono">· {c.doc_number}</span>}
                      {c.phone && <span>· +{c.phone}</span>}
                    </div>
                    <div className="text-[10px] text-slate-400 mt-0.5">
                      {c.owner_name ? `Owner: ${c.owner_name}` : ""}
                      {c.lead_status && <span className="ml-1">· <span className="uppercase tracking-wider font-bold text-emerald-700">{c.lead_status}</span></span>}
                      {c.lead_priority && <span className="ml-1">· {c.lead_priority}</span>}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-2xl tracking-tighter font-bold text-rose-600 tabular-nums" style={{fontFamily: 'Outfit, sans-serif'}} data-testid={`hot-cust-opens-${i}`}>{c.open_count}</div>
                    <div className="text-[9px] uppercase tracking-wider text-slate-500">opens</div>
                  </div>
                </div>
                <div className="flex items-center justify-between text-[10px] text-slate-400">
                  <span>Last: {(c.last_opened_at || "").slice(0, 16).replace("T", " ")}</span>
                  <button
                    data-testid={`hot-cust-followup-${i}`}
                    onClick={() => followUp(c)}
                    disabled={!c.phone}
                    className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 border border-emerald-300 text-emerald-700 hover:bg-emerald-50 inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                    title={c.phone ? "Open WhatsApp follow-up" : "No phone on customer master"}
                  >
                    <WhatsappLogo size={11} weight="fill" /> Follow-up
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      {waModal && <WhatsAppSendModal open={true} onClose={() => setWaModal(null)} {...waModal} user={user} />}
    </>
  );
};

export default HottestCustomersWidget;
